<template>
  <div class="report-preview-page">
    <div class="container-fluid py-1">
      <div class="row">
        <div class="col-12">
          <div class="card my-4">
            <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
              <div
                class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3"
              >
                <div class="d-flex justify-content-between align-items-start px-3">
                  <div>
                    <h6 class="text-white text-capitalize mb-1">Data Preview</h6>
                    <p class="text-white text-sm mb-0">
                      Select a license-based report configuration and preview its data structure
                    </p>
                  </div>
                  <button
                    class="btn btn-sm btn-light ms-2"
                    @click="openExportModal"
                    :disabled="isExportingAll || isLoading || storedConfigurations.length === 0"
                  >
                    <i class="fas fa-download me-1"></i>
                    Export All
                  </button>
                </div>
              </div>
            </div>

            <div class="card-body p-0">
              <div class="preview-content-wrapper">
                <!-- Selection Section -->
                <div class="selection-section mb-4">
                  <div class="section-header py-2">
                    <h5 class="mb-0 font-weight-bold text-dark">
                      Select License and Report
                    </h5>
                  </div>
                  <div class="section-body py-3">
                    <div class="mb-3">
                      <h6 class="text-sm text-dark mb-2">Step 1: Choose Active License</h6>
                      <div class="d-flex flex-wrap gap-2">
                        <button
                          v-for="license in availableLicenses"
                          :key="license.license_record_id"
                          class="btn btn-sm"
                          :class="selectedLicenseRecordId === license.license_record_id ? 'btn-primary' : 'btn-outline-primary'"
                          @click="onLicenseSelect(license.license_record_id)"
                        >
                          {{ license.license_name }} ({{ license.license_id }})
                        </button>
                      </div>
                    </div>

                    <div class="row g-2 align-items-stretch">
                      <div class="col-8">
                        <select
                          v-model="selectedConfigId"
                          class="form-select form-select-sm h-100"
                          @change="onReportSelect"
                          :disabled="!selectedLicenseRecordId || reportsForSelectedLicense.length === 0"
                        >
                          <option value="" disabled>Select a report</option>
                          <option
                            v-for="config in reportsForSelectedLicense"
                            :key="config.id"
                            :value="config.id"
                          >
                            {{ getReportOptionLabel(config) }}
                          </option>
                        </select>
                      </div>
                      <div class="col-4">
                        <button
                          class="btn btn-success btn-sm w-100 h-100"
                          @click="loadReportPreview"
                          :disabled="!selectedConfigId || isLoading"
                        >
                          <span v-if="isLoading">
                            <i class="fas fa-spinner fa-spin me-1"></i>
                            Loading...
                          </span>
                          <span v-else>Preview Data Format</span>
                        </button>
                      </div>
                    </div>

                  </div>
                </div>

                <!-- Preview Section -->
                <div v-if="reportData" class="preview-section">
                  <div
                    class="section-header py-2 d-flex justify-content-between align-items-center"
                  >
                    <h5 class="mb-0 font-weight-bold text-dark">
                      {{ selectedReportName }}
                      <span
                        v-if="hasGameCategories"
                        class="badge bg-primary ms-2"
                      >
                        {{ getSelectedCategoriesText() }}
                      </span>
                    </h5>
                    <small v-if="selectedReportConfig" class="text-muted">
                      {{ selectedReportMeta }}
                    </small>
                  </div>
                  <div class="section-body py-3">
                    <!-- Preview Content -->
                    <div class="preview-container">
                      <div
                        class="preview-header d-flex justify-content-between align-items-center"
                      >
                        <div></div>
                        <div class="d-flex align-items-center">
                          <div class="preview-tabs me-2">
                            <button
                              class="preview-tab"
                              :class="{ active: previewFormat === 'json' }"
                              @click="previewFormat = 'json'"
                            >
                              JSON
                            </button>
                            <button
                              class="preview-tab"
                              :class="{ active: previewFormat === 'xml' }"
                              @click="previewFormat = 'xml'"
                            >
                              XML
                            </button>
                          </div>
                          <button
                            class="btn btn-outline-secondary btn-sm me-2"
                            @click="copyToClipboard"
                            title="Copy to clipboard"
                          >
                            <i class="fas fa-copy"></i>
                          </button>
                          <button
                            class="btn btn-outline-secondary btn-sm me-2"
                            @click="toggleFullscreen"
                            title="Toggle fullscreen"
                          >
                            <i class="fas fa-expand"></i>
                          </button>
                        </div>
                      </div>

                      <div class="preview-body mt-3">
                        <!-- Fullscreen overlay with close button -->
                        <div v-if="isFullscreen" class="fullscreen-overlay">
                          <div class="fullscreen-header">
                            <div>
                              <strong>{{ selectedReportName }}</strong>
                              <span class="text-muted ms-2"
                                >(Fullscreen Mode)</span
                              >
                            </div>
                            <div class="fullscreen-controls">
                              <button
                                class="btn btn-outline-secondary btn-sm me-2"
                                @click="copyToClipboard"
                                title="Copy to clipboard"
                              >
                                <i class="fas fa-copy"></i>
                              </button>
                              <button
                                class="btn btn-danger btn-sm"
                                @click="toggleFullscreen"
                                title="Exit fullscreen"
                              >
                                <i class="fas fa-times"></i> Exit
                              </button>
                            </div>
                          </div>
                          <div class="fullscreen-content">
                            <pre
                              class="code-display-box fullscreen"
                            ><code class="hljs" v-html="highlightedPreview"></code></pre>
                          </div>
                        </div>

                        <!-- Regular preview (non-fullscreen) -->
                        <div v-else>
                          <pre
                            class="code-display-box"
                          ><code class="hljs" v-html="highlightedPreview"></code></pre>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Empty State -->
                <div v-else class="empty-section">
                  <div class="section-body py-5 text-center">
                    <div class="empty-state-icon mb-3">
                      <i class="fas fa-file-alt fa-3x text-muted"></i>
                    </div>
                    <h5 class="text-muted mb-2">No Report Selected</h5>
                    <p class="text-muted small">
                      Please select a license report from the dropdown above to preview its structure
                    </p>
                  </div>
                </div>

                <!-- Loading State -->
                <div v-if="isLoading" class="loading-section">
                  <div class="section-body py-5 text-center">
                    <div class="spinner-border text-primary mb-3" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="text-muted">Loading report data...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="modal-backdrop"
      @click="showDeleteModal = false"
    >
      <div class="modal-dialog" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Delete</h5>
            <button
              type="button"
              class="btn-close"
              @click="showDeleteModal = false"
            ></button>
          </div>
          <div class="modal-body">
            <p>Report configuration removal is managed by the Board/admin.</p>
            <p class="text-danger mb-0">
              <strong>{{ selectedReportName }}</strong>
            </p>
            <p class="text-muted small mt-2"></p>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showDeleteModal = false"
              :disabled="isDeleting"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="deleteDataMap"
              :disabled="isDeleting"
            >
              <span v-if="isDeleting">
                <i class="fas fa-spinner fa-spin me-1"></i>
                Deleting...
              </span>
              <span v-else>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showExportModal"
      class="modal-backdrop"
      @click="showExportModal = false"
    >
      <div class="modal-dialog" @click.stop>
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Export All Report Configurations</h5>
            <button
              type="button"
              class="btn-close"
              @click="showExportModal = false"
            ></button>
          </div>
          <div class="modal-body">
            <p class="mb-2">
              You are about to download one file containing configurations for all reports under your approved licenses.
            </p>
            <p class="small text-muted mb-2">
              Total licenses: <strong>{{ exportSummary.totalLicenses }}</strong> |
              Total reports: <strong>{{ exportSummary.totalReports }}</strong>
            </p>

            <label class="form-label small fw-bold">Choose file format</label>
            <select v-model="bulkExportFormat" class="form-select form-select-sm mb-3">
              <option value="json">JSON</option>
              <option value="xml">XML</option>
            </select>

            <div class="small">
              <strong>Reports included:</strong>
              <div class="mt-1 export-report-list">
                <span
                  v-for="reportName in exportSummary.reportNames"
                  :key="reportName"
                  class="badge bg-light text-dark border me-1 mb-1"
                >
                  {{ reportName }}
                </span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showExportModal = false"
              :disabled="isExportingAll"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="confirmExportAll"
              :disabled="isExportingAll"
            >
              <span v-if="isExportingAll">
                <i class="fas fa-spinner fa-spin me-1"></i>
                Exporting...
              </span>
              <span v-else>Download File</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { resolveFieldHelp } from "@/data/ncemsFieldHelp";
import ReportConfiguration from "@/views/report_configuration.vue";

export default {
  name: "ReportPreview",
  data() {
    return {
      selectedLicenseRecordId: "",
      selectedConfigId: "", // Now stores database ID instead of report_id
      previewFormat: "json",
      reportData: null,
      storedConfigurations: [],
      isLoading: false,
      isDeleting: false,
      errorMessage: "",
      successMessage: "",
      isFullscreen: false,
      lastUpdated: null,
      showDeleteModal: false,
      bulkExportFormat: "json",
      isExportingAll: false,
      showExportModal: false,
    };
  },
  computed: {
    exportSummary() {
      const licenses = new Set();
      const reports = new Set();
      this.storedConfigurations.forEach((config) => {
        licenses.add(config.license_record_id);
        reports.add(config.report_name);
      });
      return {
        totalLicenses: licenses.size,
        totalReports: this.storedConfigurations.length,
        reportNames: Array.from(reports).sort((a, b) => a.localeCompare(b)),
      };
    },
    availableLicenses() {
      const byLicense = {};
      this.storedConfigurations.forEach((config) => {
        if (!byLicense[config.license_record_id]) {
          byLicense[config.license_record_id] = {
            license_record_id: config.license_record_id,
            license_name: config.license_name,
            license_id: config.license_id,
            group_label: config.group_label,
          };
        }
      });
      return Object.values(byLicense);
    },
    reportsForSelectedLicense() {
      if (!this.selectedLicenseRecordId) return [];
      return this.storedConfigurations.filter(
        (config) => config.license_record_id === this.selectedLicenseRecordId,
      );
    },
    selectedReportName() {
      if (!this.selectedConfigId) return "";
      const config = this.storedConfigurations.find(
        (config) => config.id === this.selectedConfigId,
      );
      return config ? config.report_name : "";
    },
    selectedReportConfig() {
      if (!this.selectedConfigId) return null;
      return this.storedConfigurations.find(
        (config) => config.id === this.selectedConfigId,
      );
    },
    selectedReportId() {
      // Get the report_id from the selected config for XML generation
      return this.selectedReportConfig
        ? this.selectedReportConfig.report_id
        : "";
    },
    selectedReportMeta() {
      return this.getSelectedConfigMetaLine(this.selectedReportConfig);
    },
    hasGameCategories() {
      return (
        this.reportData &&
        this.reportData.gameCategories &&
        this.reportData.gameCategories.length > 0
      );
    },
    jsonPreview() {
      if (!this.reportData) return "No data available";

      try {
        if (this.selectedReportConfig && this.selectedReportConfig.data_map) {
          let dataMap = this.selectedReportConfig.data_map;

          // If data_map is still a string (with comments), strip and parse
          if (typeof dataMap === "string") {
            try {
              dataMap = JSON.parse(this.stripJsonComments(dataMap));
            } catch (e) {
              // If parsing still fails, return the raw string
              return dataMap;
            }
          }

          // Ensure correct key order for known report types
          dataMap = this.reorderDataMapKeys(dataMap, this.selectedReportId);

          let formatted = JSON.stringify(dataMap, null, 2);

          // Re-inject known comments based on report type
          formatted = this.addJsonComments(formatted, this.selectedReportId);

          return formatted;
        }

        if (typeof this.reportData === "object") {
          return JSON.stringify(this.reportData, null, 2);
        }

        return this.reportData;
      } catch (error) {
        console.error("Error formatting JSON:", error);
        return "Error formatting JSON data";
      }
    },
    xmlPreview() {
      if (!this.reportData) return "";

      const reportId = this.selectedReportId;
      const generators = this.getXmlStructureGenerators();
      if (generators && generators[reportId]) {
        return generators[reportId]();
      }

      if (this.selectedReportConfig && this.selectedReportConfig.data_map) {
        return this.convertJsonToXml(this.selectedReportConfig.data_map);
      }

      return `<data>\n  <reportId>${reportId}</reportId>\n  <message>XML preview not available for this report</message>\n</data>`;
    },

    highlightedPreview() {
      if (!this.reportData) return "";

      let code =
        this.previewFormat === "json" ? this.jsonPreview : this.xmlPreview;

      if (this.previewFormat === "xml") {
        code = this.addXmlListComments(code, this.selectedReportId);
      }

      // Use "javascript" for JSON with comments so // comments get highlighted properly
      let language = this.previewFormat === "json" ? "json" : "xml";
      if (language === "json" && code.includes("//")) {
        language = "javascript";
      }

      if (typeof window !== "undefined" && window.hljs) {
        try {
          const result = window.hljs.highlight(code, { language: language });
          return this.annotateFieldHelp(
            result.value,
            this.previewFormat,
            this.selectedReportId,
          );
        } catch (e) {
          console.warn("Highlight.js highlighting failed:", e);
        }
      }

      return this.escapeHtml(code);
    },
  },
  created() {
    this.loadStoredConfigurations();
  },
  methods: {
    stripJsonComments(jsonString) {
      let result = "";
      let inString = false;
      let stringChar = "";
      let i = 0;

      while (i < jsonString.length) {
        const char = jsonString[i];
        const nextChar = jsonString[i + 1];

        if (inString) {
          result += char;
          if (char === "\\") {
            i++;
            if (i < jsonString.length) result += jsonString[i];
          } else if (char === stringChar) {
            inString = false;
          }
        } else {
          if (char === '"' || char === "'") {
            inString = true;
            stringChar = char;
            result += char;
          } else if (char === "/" && nextChar === "/") {
            i++;
            while (i < jsonString.length && jsonString[i] !== "\n") {
              i++;
            }
            continue;
          } else if (char === "/" && nextChar === "*") {
            i += 2;
            while (
              i < jsonString.length - 1 &&
              !(jsonString[i] === "*" && jsonString[i + 1] === "/")
            ) {
              i++;
            }
            i += 2;
            continue;
          } else {
            result += char;
          }
        }
        i++;
      }
      return result;
    },

    addJsonComments(jsonStr, reportId) {
      if (!jsonStr || !reportId) return jsonStr;

      // Define comment injections per report type.
      // Each entry: { before: "target string to find", comment: "the comment line(s) to insert", indent: "whitespace before key after comment" }
      // The indent must match exactly what report_configuration.vue produces
      const commentMap = {
        terminal_config: [
          {
            before: '"installedGameList": [',
            comment:
              "\n // Only for landTable category, include installedGameList with game details:\n  ",
          },
          {
            before: '"slotTerminal": {',
            comment:
              "\n // Only for landSlot or EGMs category, include slotTerminal details:\n  ",
          },
          {
            before: '"rouletteWheel": {',
            comment:
              "\n // For Roulette tables, add rouletteWheel object in terminalConfigDetail\n  ",
          },
        ],
        land_summary: [
          {
            before: '"movementList": {',
            comment:
              "\n // Only for landSlot category, include movementList with transaction details:\n  ",
          },
          {
            before: '"meterList": {',
            comment:
              "\n // Only for landSlot category, include meterList with meter details:\n  ",
          },
          {
            before: '"landGameList": {',
            comment:
              "\n // Only for landTable category, include landGameList with game and movement details:\n  ",
          },
        ],
        game_summary: [
          {
            before: '"gameSummaryDetail": {',
            comment:
              "\n // gameSummaryDetail will include different metrics based on selected game categories. \n // Only include relevant sections for the selected category in a record.\n  ",
          },
          {
            before: '"gameMovementList": [',
            comment:
              "\n // gameMovementList is optional. An empty list can be given\n  ",
          },
        ],
        game_funds_in_play: [
          {
            before: '"gameInPlayDetail": {',
            comment:
              "\n // gameInPlayDetail will include different metrics based on selected game categories. \n // Only include relevant sections for the selected category in a record.\n  ",
          },
        ],
        player_registration: [
          {
            before: '"playerIdentificationList": [',
            comment:
              "\n   // This playerIdentificationList is optional. An empty list can be provided,\n       ",
          },
          {
            before: '"playerExclusionList": [',
            comment:
              "\n   // This playerExclusionList is optional. An empty list can be provided,\n       ",
          },
        ],
        player_activity: [
          {
            before: '"transferee": {',
            comment:
              "\n   // transferee is optional. Can be excluded \n       ",
          },
          {
            before: '"startingBalanceList": [',
            comment:
              "\n   // startingBalanceList is optional. An empty list can be provided,\n       ",
          },
          {
            before: '"playerMovementList": [',
            comment:
              "\n   // playerMovementList is optional. An empty list can be provided,\n       ",
          },
          {
            before: '"endingBalanceList": [',
            comment:
              "\n   // endingBalanceList is optional. An empty list can be provided,\n       ",
          },
        ],
        land_event: [
          {
            before: '"voucherData": {',
            comment:
              "\n   // voucherData is required for RRI_voucherIssued & RRI_voucherRedeemed eventCodes. \n       ",
          },
          {
            before: '"gameOutcomeData": {',
            comment:
              "\n   // gameOutcomeData is required for RRI_gameOutcome & RRI_progressiveAward eventCodes. \n       ",
          },
          {
            before: '"handpayDisbursementData": {',
            comment:
              "\n   // handpayDisbursementData is required for RRI_handpayDisbursement eventCode. \n       ",
          },
          {
            before: '"noteAcceptedData": {',
            comment:
              "\n   // noteAcceptedData is required for RRI_noteAccepted eventCode. \n       ",
          },
          {
            before: '"watData": {',
            comment:
              "\n   // watData is required for RRI_watCompleted eventCode. \n       ",
          },
        ],
      };

      const injections = commentMap[reportId];
      if (!injections) return jsonStr;

      let result = jsonStr;
      injections.forEach(({ before, comment }) => {
        // Only replace the first occurrence
        const idx = result.indexOf(before);
        if (idx !== -1) {
          result = result.substring(0, idx) + comment + result.substring(idx);
        }
      });

      return result;
    },

    reorderDataMapKeys(dataMap, reportId) {
      // Helper: reorder an object's keys to match a given order.
      // Keys not in the order list are appended at the end.
      const reorder = (obj, keyOrder) => {
        if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
        const ordered = {};
        keyOrder.forEach((key) => {
          if (key in obj) ordered[key] = obj[key];
        });
        // Append any remaining keys not in the order list
        Object.keys(obj).forEach((key) => {
          if (!(key in ordered)) ordered[key] = obj[key];
        });
        return ordered;
      };

      // Recursively reorder nested objects based on nested key definitions
      const reorderNested = (obj, nestedOrders) => {
        if (!obj || typeof obj !== "object" || !nestedOrders) return obj;
        let result = obj;
        Object.entries(nestedOrders).forEach(([nestedKey, nestedKeyOrder]) => {
          if (
            result[nestedKey] &&
            typeof result[nestedKey] === "object" &&
            !Array.isArray(result[nestedKey])
          ) {
            result[nestedKey] = reorder(result[nestedKey], nestedKeyOrder);
          }
        });
        return result;
      };

      const keyOrders = {
        terminal_config: {
          root: ["terminalConfig"],
          item: [
            "brandId",
            "terminal",
            "model",
            "terminalConfigDetail",
            "installedGameList",
          ],
          nested: {
            terminal: ["terminalId"],
            model: ["modelId"],
            terminalConfigDetail: [
              "terminalName",
              "regulatorId",
              "serialNum",
              "isDeleted",
              "fromDateTime",
              "toDateTime",
              "slotTerminal",
              "rouletteWheel",
            ],
          },
        },
        land_summary: {
          root: ["landSummary"],
          item: [
            "brandId",
            "terminal",
            "movementList",
            "meterList",
            "landGameList",
          ],
          nested: {
            terminal: ["terminalId"],
          },
        },
        game_summary: {
          root: ["gameSummary"],
          item: ["brandId", "game", "gameSummaryDetail", "gameMovementList"],
          nested: {
            game: [
              "gameId",
              "gameName",
              "gameCategory",
              "gameType",
              "gameVariant",
            ],
          },
        },
        game_funds_in_play: {
          root: ["gameFundsInPlay"],
          item: [
            "periodStart",
            "statusDateTime",
            "brandId",
            "game",
            "gameInPlayDetail",
            "gameMovementList",
          ],
          nested: {
            game: [
              "gameId",
              "gameName",
              "gameCategory",
              "gameType",
              "gameVariant",
            ],
          },
        },
        player_summary: {
          root: ["playerSummary"],
          item: ["brandId", "player", "playerGameSummary"],
          nested: {
            player: ["playerId"],
          },
        },
        player_registration: {
          root: ["playerRegistration"],
          item: [
            "brandId",
            "transDateTime",
            "player",
            "playerInfo",
            "playerStatusInfo",
            "playerPhoneList",
            "playerIdentificationList",
            "playerExclusionList",
          ],
          nested: {
            player: ["playerId"],
            playerInfo: [
              "givenNames",
              "familyNames",
              "nationality",
              "birthDate",
              "genderType",
              "fromDateTime",
            ],
            playerStatusInfo: [
              "playerStatus",
              "operatorStatus",
              "fromDateTime",
              "verifiedDateTime",
            ],
          },
        },
        player_activity: {
          root: ["playerActivity"],
          item: [
            "brandId",
            "player",
            "playerActivityDetail",
            "startingBalanceList",
            "playerMovementList",
            "endingBalanceList",
          ],
          nested: {
            player: ["playerId"],
            playerActivityDetail: [
              "playerActivity",
              "playerActivityDesc",
              "playerActivityDateTime",
              "transferee",
            ],
          },
        },
        land_event: {
          root: ["landEvent"],
          item: ["brandId", "terminal", "event", "associatedData"],
          nested: {
            terminal: ["terminalId"],
            event: ["eventId", "eventCode", "eventText", "eventDateTime"],
            associatedData: [
              "voucherData",
              "gameOutcomeData",
              "handpayDisbursementData",
              "noteAcceptedData",
              "watData",
            ],
          },
        },
        jackpot_balance: {
          root: ["jackpotBalance"],
          item: [
            "statusDateTime",
            "brandId",
            "controller",
            "jackpotOpeningList",
            "jackpotGameList",
            "jackpotActivityList",
            "jackpotClosingList",
          ],
          nested: {
            controller: ["serverId", "controllerId"],
          },
        },
      };

      const order = keyOrders[reportId];
      if (!order) return dataMap;

      // Reorder root keys
      const result = reorder(dataMap, order.root);

      // Reorder items inside the main array
      const rootKey = order.root.find((k) => result[k]);
      if (rootKey && Array.isArray(result[rootKey])) {
        result[rootKey] = result[rootKey].map((item) => {
          // Reorder top-level item keys
          let ordered = reorder(item, order.item);
          // Reorder nested object keys
          if (order.nested) {
            ordered = reorderNested(ordered, order.nested);
          }
          return ordered;
        });
      }

      return result;
    },

    async loadStoredConfigurations() {
      this.isLoading = true;
      try {
        const [contextResponse, selectionsResponse] = await Promise.all([
          axios.get("/report-config/context/"),
          axios.get("/report-config/selections/"),
        ]);

        const savedByLicenseReport = {};
        if (selectionsResponse.status === 200 && selectionsResponse.data) {
          (selectionsResponse.data.licenses || []).forEach((licenseItem) => {
            (licenseItem.reports || []).forEach((report) => {
              const key = `${licenseItem.license_record_id}::${report.report_id}`;
              savedByLicenseReport[key] = {
                game_unique_names: report.game_unique_names || [],
                updated_at: report.updated_at || null,
                category_count: Number(report.category_count || 0),
                is_configurable: !!report.is_configurable,
              };
            });
          });
        }

        const flattened = [];
        if (contextResponse.status === 200 && contextResponse.data) {
          (contextResponse.data.groups || []).forEach((groupItem) => {
            (groupItem.licenses || []).forEach((licenseItem) => {
              (licenseItem.reports || []).forEach((report) => {
                const key = `${licenseItem.license_record_id}::${report.report_id}`;
                const saved = savedByLicenseReport[key] || {};
                flattened.push({
                  id: `lic-${licenseItem.license_record_id}-${report.report_id}`,
                  report_id: report.report_id,
                  report_name: report.report_name,
                  license_record_id: licenseItem.license_record_id,
                  license_code: licenseItem.license_code,
                  license_name: licenseItem.license_name,
                  license_id: licenseItem.license_id,
                  group: licenseItem.group,
                  group_label: licenseItem.group_label,
                  is_configurable: !!report.is_configurable,
                  requires_category: !!report.requires_category,
                  allowed_game_unique_names: report.allowed_game_unique_names || [],
                  game_unique_names: saved.game_unique_names || [],
                  is_configured: !!savedByLicenseReport[key],
                  category_count: Number(saved.category_count || 0),
                  updated_at: saved.updated_at || null,
                  license_label: `${licenseItem.group_label} | ${licenseItem.license_name} (${licenseItem.license_id})`,
                });
              });
            });
          });
        }

        this.storedConfigurations = flattened;
        if (flattened.length > 0) {
          this.selectedLicenseRecordId = flattened[0].license_record_id;
        }
      } catch (error) {
        console.error("Error loading stored configurations:", error);
        this.errorMessage = "Failed to load report configurations";
      } finally {
        this.isLoading = false;
      }
    },

    onLicenseSelect(licenseRecordId) {
      this.selectedLicenseRecordId = licenseRecordId;
      this.selectedConfigId = "";
      this.reportData = null;
      this.errorMessage = "";
      this.successMessage = "";
    },

    async loadReportPreview() {
      if (!this.selectedConfigId) {
        this.errorMessage = "Please select a report type first";
        return;
      }

      this.isLoading = true;
      this.errorMessage = "";
      this.successMessage = "";

      try {
        const config = this.storedConfigurations.find(
          (item) => item.id === this.selectedConfigId,
        );

        if (config) {
          const categoryIds = config.game_unique_names || [];
          config.data_map = this.buildFullReportLayout(config.report_id, categoryIds);

          this.reportData = config.data_map;
          this.lastUpdated = new Date();
          this.successMessage = "Report loaded successfully";

          setTimeout(() => {
            const previewSection = document.querySelector(".preview-container");
            if (previewSection) {
              previewSection.scrollIntoView({ behavior: "smooth" });
            }
          }, 100);
        } else {
          this.errorMessage = "No configuration found for this report";
          this.reportData = null;
        }
      } catch (error) {
        console.error("Error loading report preview:", error);
        this.errorMessage = "Failed to load report preview";
        this.reportData = null;
      } finally {
        this.isLoading = false;
      }
    },

    onReportSelect() {
      this.reportData = null;
      this.errorMessage = "";
      this.successMessage = "";
      if (this.selectedConfigId) {
        this.loadReportPreview();
      }
    },

    getAllCategoriesForPreview() {
      if (
        ReportConfiguration &&
        ReportConfiguration.methods &&
        typeof ReportConfiguration.methods.getAllCategories === "function"
      ) {
        return ReportConfiguration.methods.getAllCategories.call({});
      }
      return [];
    },

    buildFullReportLayout(reportId, categoryIds = []) {
      try {
        const selectedCategories = (categoryIds || []).map((categoryId) => ({
          reportId,
          categoryId,
        }));

        const reportJsonBuilder =
          ReportConfiguration &&
          ReportConfiguration.methods &&
          ReportConfiguration.methods.getReportJsonStructure;

        if (typeof reportJsonBuilder !== "function") {
          return {
            reportId,
            gameCategories: (categoryIds || []).map((categoryId) => ({
              gameCategory: categoryId,
              gameCategoryName: this.getCategoryName(categoryId),
            })),
          };
        }

        const ctx = {
          getSelectedCategoriesForReport: (id) =>
            selectedCategories.filter((cat) => cat.reportId === id),
          getAllCategories: () => this.getAllCategoriesForPreview(),
        };

        const generatedJson = reportJsonBuilder.call(ctx, reportId);
        if (!generatedJson) {
          return {
            reportId,
            gameCategories: (categoryIds || []).map((categoryId) => ({
              gameCategory: categoryId,
              gameCategoryName: this.getCategoryName(categoryId),
            })),
          };
        }

        return JSON.parse(this.stripJsonComments(generatedJson));
      } catch (error) {
        console.error("Error building full report layout:", error);
        return {
          reportId,
          gameCategories: (categoryIds || []).map((categoryId) => ({
            gameCategory: categoryId,
            gameCategoryName: this.getCategoryName(categoryId),
          })),
        };
      }
    },
    getReportOptionLabel(config) {
      if (!config.is_configurable) {
        return config.report_name;
      }
      if (!config.is_configured) return config.report_name;
      if (config.category_count > 0) {
        return `${config.report_name} (Configured: ${config.category_count})`;
      }
      return `${config.report_name} (Configured)`;
    },
    formatTimestamp(value) {
      if (!value) return "";
      const dt = new Date(value);
      if (Number.isNaN(dt.getTime())) return "";
      return dt.toLocaleString();
    },
    getSelectedConfigMetaLine(config) {
      if (!config) return "";
      const bits = [];
      if (config.is_configurable && config.is_configured) {
        bits.push(`Configured categories: ${config.category_count || 0}`);
      }
      if (config.updated_at) {
        bits.push(`Updated: ${this.formatTimestamp(config.updated_at)}`);
      }
      return bits.join(" | ");
    },


    getSelectedCategoriesText() {
      if (!this.reportData || !this.reportData.gameCategories) return "";
      return this.reportData.gameCategories
        .map((cat) => cat.gameCategoryName || cat.gameCategory)
        .join(", ");
    },

    getXmlStructureGenerators() {
      const createXmlTag = (
        tagName,
        value,
        indent = 0,
        selfClosing = false,
        attributes = {},
      ) => {
        const indentStr = "  ".repeat(indent);

        if (selfClosing) {
          if (Object.keys(attributes).length > 0) {
            const attrs = Object.entries(attributes)
              .map(([key, val]) => `${key}="${val}"`)
              .join(" ");
            return `${indentStr}<${tagName} ${attrs} />`;
          }
          return `${indentStr}<${tagName} />`;
        }

        if (value === undefined) {
          return `${indentStr}<${tagName}>`;
        }

        return `${indentStr}<${tagName}>${value}</${tagName}>`;
      };

      return {
        game_summary: () => {
          const config = this.selectedReportConfig;
          if (!config || !config.data_map) return "";

          const xmlLines = ["<data>"];
          xmlLines.push("  <gameSummary>");

          if (
            config.data_map.gameSummary &&
            Array.isArray(config.data_map.gameSummary)
          ) {
            config.data_map.gameSummary.forEach((summary) => {
              xmlLines.push(
                createXmlTag("periodStart", summary.periodStart, 4),
              );
              xmlLines.push(createXmlTag("brandId", summary.brandId, 4));

              xmlLines.push("    <game>");
              if (summary.game) {
                xmlLines.push(createXmlTag("gameId", summary.game.gameId, 6));
                xmlLines.push(
                  createXmlTag("gameName", summary.game.gameName, 6),
                );
                xmlLines.push(
                  createXmlTag("gameCategory", summary.game.gameCategory, 6),
                );
                xmlLines.push(
                  createXmlTag("gameType", summary.game.gameType, 6),
                );
                xmlLines.push(
                  createXmlTag(
                    "gameVariant",
                    summary.game.gameVariant || "null",
                    6,
                  ),
                );
              }
              xmlLines.push("    </game>");

              if (summary.gameSummaryDetail) {
                xmlLines.push("    <gameSummaryDetail>");
                Object.entries(summary.gameSummaryDetail).forEach(
                  ([key, detail]) => {
                    xmlLines.push(`      <${key}>`);
                    Object.entries(detail).forEach(([field, value]) => {
                      xmlLines.push(createXmlTag(field, value, 8));
                    });
                    xmlLines.push(`      </${key}>`);
                  },
                );
                xmlLines.push("    </gameSummaryDetail>");
              }

              if (
                summary.gameMovementList &&
                Array.isArray(summary.gameMovementList)
              ) {
                xmlLines.push("    <gameMovementList>");
                summary.gameMovementList.forEach((movement) => {
                  xmlLines.push("      <gameMovement>");
                  xmlLines.push(
                    createXmlTag("transClass", movement.transClass, 8),
                  );
                  xmlLines.push(
                    createXmlTag("transType", movement.transType, 8),
                  );
                  xmlLines.push(
                    createXmlTag("fundsType", movement.fundsType, 8),
                  );
                  xmlLines.push(createXmlTag("transAmt", movement.transAmt, 8));
                  xmlLines.push("      </gameMovement>");
                });
                xmlLines.push("    </gameMovementList>");
              }
            });
          }

          xmlLines.push("  </gameSummary>");
          xmlLines.push("</data>");
          return xmlLines.join("\n");
        },

        game_funds_in_play: () => {
          const config = this.selectedReportConfig;
          if (!config || !config.data_map) return "";

          const xmlLines = ["<data>"];
          xmlLines.push("  <gameFundsInPlay>");

          if (
            config.data_map.gameFundsInPlay &&
            Array.isArray(config.data_map.gameFundsInPlay)
          ) {
            config.data_map.gameFundsInPlay.forEach((funds) => {
              xmlLines.push(createXmlTag("periodStart", funds.periodStart, 4));
              xmlLines.push(
                createXmlTag("statusDateTime", funds.statusDateTime, 4),
              );
              xmlLines.push(createXmlTag("brandId", funds.brandId, 4));

              xmlLines.push("    <game>");
              if (funds.game) {
                xmlLines.push(createXmlTag("gameId", funds.game.gameId, 6));
                xmlLines.push(createXmlTag("gameName", funds.game.gameName, 6));
                xmlLines.push(
                  createXmlTag("gameCategory", funds.game.gameCategory, 6),
                );
                xmlLines.push(createXmlTag("gameType", funds.game.gameType, 6));
                xmlLines.push(
                  createXmlTag(
                    "gameVariant",
                    funds.game.gameVariant || "null",
                    6,
                  ),
                );
              }
              xmlLines.push("    </game>");

              if (funds.gameInPlayDetail) {
                xmlLines.push("    <gameInPlayDetail>");
                Object.entries(funds.gameInPlayDetail).forEach(
                  ([key, detail]) => {
                    xmlLines.push(`      <${key}>`);
                    Object.entries(detail).forEach(([field, value]) => {
                      xmlLines.push(createXmlTag(field, value, 8));
                    });
                    xmlLines.push(`      </${key}>`);
                  },
                );
                xmlLines.push("    </gameInPlayDetail>");
              }

              if (
                funds.gameMovementList &&
                Array.isArray(funds.gameMovementList)
              ) {
                xmlLines.push("    <gameMovementList>");
                funds.gameMovementList.forEach((movement) => {
                  xmlLines.push("      <gameMovement>");
                  xmlLines.push(
                    createXmlTag("transClass", movement.transClass, 8),
                  );
                  xmlLines.push(
                    createXmlTag("transType", movement.transType, 8),
                  );
                  xmlLines.push(
                    createXmlTag("fundsType", movement.fundsType, 8),
                  );
                  xmlLines.push(createXmlTag("transAmt", movement.transAmt, 8));
                  xmlLines.push("      </gameMovement>");
                });
                xmlLines.push("    </gameMovementList>");
              }
            });
          }

          xmlLines.push("  </gameFundsInPlay>");
          xmlLines.push("</data>");
          return xmlLines.join("\n");
        },

        land_summary: () => {
          const config = this.selectedReportConfig;
          if (!config || !config.data_map) return "";

          const xmlLines = ["<data>"];
          xmlLines.push("  <landSummary>");

          if (
            config.data_map.landSummary &&
            Array.isArray(config.data_map.landSummary)
          ) {
            config.data_map.landSummary.forEach((summary) => {
              xmlLines.push(
                createXmlTag("periodStart", summary.periodStart, 4),
              );
              xmlLines.push(createXmlTag("brandId", summary.brandId, 4));

              xmlLines.push("    <terminal>");
              if (summary.terminal) {
                xmlLines.push(
                  createXmlTag("terminalId", summary.terminal.terminalId, 6),
                );
              }
              xmlLines.push("    </terminal>");

              if (summary.movementList) {
                xmlLines.push("    <movementList>");

                xmlLines.push("      <movements>");
                if (
                  summary.movementList.movements &&
                  Array.isArray(summary.movementList.movements)
                ) {
                  summary.movementList.movements.forEach((movement) => {
                    xmlLines.push("        <movement>");
                    xmlLines.push(
                      createXmlTag("transClass", movement.transClass, 10),
                    );
                    xmlLines.push(
                      createXmlTag("transType", movement.transType, 10),
                    );
                    xmlLines.push(
                      createXmlTag("movementValue", movement.movementValue, 10),
                    );
                    xmlLines.push("        </movement>");
                  });
                }
                xmlLines.push("      </movements>");
                xmlLines.push(
                  createXmlTag(
                    "startDateTime",
                    summary.movementList.startDateTime,
                    6,
                  ),
                );
                xmlLines.push(
                  createXmlTag(
                    "endDateTime",
                    summary.movementList.endDateTime,
                    6,
                  ),
                );
                xmlLines.push("    </movementList>");
              }

              if (summary.meterList) {
                xmlLines.push("    <meterList>");
                xmlLines.push(
                  createXmlTag(
                    "statusDateTime",
                    summary.meterList.statusDateTime,
                    6,
                  ),
                );
                xmlLines.push("      <meters>");
                if (
                  summary.meterList.meters &&
                  Array.isArray(summary.meterList.meters)
                ) {
                  summary.meterList.meters.forEach((meter) => {
                    xmlLines.push("        <meter>");
                    xmlLines.push(
                      createXmlTag("transClass", meter.transClass, 10),
                    );
                    xmlLines.push(
                      createXmlTag("transType", meter.transType, 10),
                    );
                    xmlLines.push(
                      createXmlTag("meterValue", meter.meterValue, 10),
                    );
                    xmlLines.push("        </meter>");
                  });
                }
                xmlLines.push("      </meters>");
                xmlLines.push("    </meterList>");
              }
            });
          }

          xmlLines.push("  </landSummary>");
          xmlLines.push("</data>");
          return xmlLines.join("\n");
        },

        terminal_config: () => {
          const config = this.selectedReportConfig;
          if (!config || !config.data_map) return "";

          const xmlLines = ["<data>"];
          xmlLines.push("  <terminalConfig>");

          if (
            config.data_map.terminalConfig &&
            Array.isArray(config.data_map.terminalConfig)
          ) {
            config.data_map.terminalConfig.forEach((config) => {
              xmlLines.push(createXmlTag("periodStart", config.periodStart, 4));
              xmlLines.push(createXmlTag("brandId", config.brandId, 4));

              xmlLines.push("    <terminal>");
              if (config.terminal) {
                xmlLines.push(
                  createXmlTag("terminalId", config.terminal.terminalId, 6),
                );
              }
              xmlLines.push("    </terminal>");

              xmlLines.push("    <model>");
              if (config.model) {
                xmlLines.push(createXmlTag("modelId", config.model.modelId, 6));
              }
              xmlLines.push("    </model>");

              if (config.terminalConfigDetail) {
                xmlLines.push("    <terminalConfigDetail>");
                Object.entries(config.terminalConfigDetail).forEach(
                  ([key, value]) => {
                    if (key === "slotTerminal" && typeof value === "object") {
                      xmlLines.push("      <slotTerminal>");
                      Object.entries(value).forEach(([field, val]) => {
                        xmlLines.push(createXmlTag(field, val, 8));
                      });
                      xmlLines.push("      </slotTerminal>");
                    } else if (
                      key === "rouletteWheel" &&
                      typeof value === "object"
                    ) {
                      xmlLines.push("      <rouletteWheel>");
                      Object.entries(value).forEach(([field, val]) => {
                        xmlLines.push(createXmlTag(field, val, 8));
                      });
                      xmlLines.push("      </rouletteWheel>");
                    } else {
                      xmlLines.push(createXmlTag(key, value, 6));
                    }
                  },
                );
                xmlLines.push("    </terminalConfigDetail>");
              }

              if (
                config.installedGameList &&
                Array.isArray(config.installedGameList)
              ) {
                xmlLines.push("    <installedGameList>");
                config.installedGameList.forEach((game) => {
                  xmlLines.push("      <installedGame>");
                  if (game.game) {
                    xmlLines.push("        <game>");
                    xmlLines.push(createXmlTag("gameId", game.game.gameId, 10));
                    xmlLines.push(
                      createXmlTag("gameName", game.game.gameName, 10),
                    );
                    xmlLines.push(
                      createXmlTag("gameCategory", game.game.gameCategory, 10),
                    );
                    xmlLines.push(
                      createXmlTag("gameType", game.game.gameType, 10),
                    );
                    xmlLines.push("        </game>");
                  }
                  xmlLines.push("      </installedGame>");
                });
                xmlLines.push("    </installedGameList>");
              }
            });
          }

          xmlLines.push("  </terminalConfig>");
          xmlLines.push("</data>");
          return xmlLines.join("\n");
        },
      };
    },

    convertJsonToXml(obj, indent = 0) {
      const indentStr = "  ".repeat(indent);
      let xml = "";

      if (indent === 0) {
        xml += "<data>\n";
      }

      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          xml += `${indentStr}<item${index > 0 ? ` index="${index}"` : ""}>\n`;
          xml += this.convertJsonToXml(item, indent + 1);
          xml += `${indentStr}</item>\n`;
        });
      } else if (typeof obj === "object" && obj !== null) {
        Object.entries(obj).forEach(([key, value]) => {
          if (value === null || value === undefined) {
            xml += `${indentStr}<${key} />\n`;
          } else if (typeof value === "object") {
            xml += `${indentStr}<${key}>\n`;
            xml += this.convertJsonToXml(value, indent + 1);
            xml += `${indentStr}</${key}>\n`;
          } else {
            xml += `${indentStr}<${key}>${this.escapeXml(
              value.toString(),
            )}</${key}>\n`;
          }
        });
      } else if (obj !== null && obj !== undefined) {
        xml += `${indentStr}<value>${this.escapeXml(obj.toString())}</value>\n`;
      }

      if (indent === 0) {
        xml += "</data>";
      }

      return xml;
    },

    escapeXml(text) {
      if (!text) return "";
      return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;");
    },

    formatXml(xml) {
      const formatted = xml
        .replace(/(>)(<)(\/*)/g, "$1\r\n$2$3")
        .replace(/^(.*)$/gm, (match) => {
          const indent =
            (match.match(/^<\//) ? -1 : 0) +
            (match.match(/^<\w[^>]*[^/]>.*$/g) ? 1 : 0);
          return "  ".repeat(Math.max(0, indent)) + match;
        });
      return formatted;
    },

    async copyToClipboard() {
      const text =
        this.previewFormat === "json" ? this.jsonPreview : this.xmlPreview;

      try {
        await navigator.clipboard.writeText(text);
        this.showNotification("Copied to clipboard!", "success");
      } catch (err) {
        console.error("Failed to copy:", err);
        this.showNotification("Failed to copy to clipboard", "error");
      }
    },

    buildAllConfigurationsPayload() {
      const groupedByLicense = {};
      this.storedConfigurations.forEach((config) => {
        const key = String(config.license_record_id);
        if (!groupedByLicense[key]) {
          groupedByLicense[key] = {
            license_record_id: config.license_record_id,
            license_name: config.license_name,
            license_code: config.license_code,
            license_id: config.license_id,
            group: config.group,
            group_label: config.group_label,
            reports: [],
          };
        }

        const reportLayout = this.buildFullReportLayout(
          config.report_id,
          config.game_unique_names || [],
        );

        groupedByLicense[key].reports.push({
          report_id: config.report_id,
          report_name: config.report_name,
          is_configurable: !!config.is_configurable,
          game_unique_names: config.game_unique_names || [],
          configuration: reportLayout,
        });
      });

      const licenses = Object.values(groupedByLicense).sort((a, b) => {
        if (a.group !== b.group) return a.group.localeCompare(b.group);
        if (a.license_name !== b.license_name) {
          return a.license_name.localeCompare(b.license_name);
        }
        return String(a.license_id).localeCompare(String(b.license_id));
      });
      licenses.forEach((license) => {
        license.reports.sort((a, b) => a.report_name.localeCompare(b.report_name));
      });

      return {
        readme: {
          purpose:
            "This file contains all report configuration blueprints for the operator's approved licenses.",
          how_to_use: [
            "Start at licenses[]. Each license section contains only reports that belong to that license.",
            "For each report, check selectedGameCategories first (if any), then use configuration as the payload blueprint.",
            "Reports marked is_configurable=true are the ones where game categories drive payload shape.",
            "Auto-saved reports are still included so you have a complete package in one file.",
          ],
          where_to_start: "licenses[0] -> reports[0]",
        },
        generated_at: new Date().toISOString(),
        total_licenses: licenses.length,
        total_reports: licenses.reduce((acc, lic) => acc + lic.reports.length, 0),
        licenses,
      };
    },

    buildAllConfigurationsXml(payload) {
      const escapeXml = (value = "") =>
        String(value)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&apos;");

      const lines = ['<?xml version="1.0" encoding="UTF-8"?>'];
      lines.push("<!--");
      lines.push("  OPERATOR REPORT CONFIGURATION EXPORT");
      lines.push("  - Start at each <license> block.");
      lines.push("  - Inside each license, read <report> blocks.");
      lines.push("  - <selectedGameCategories> shows chosen categories (if any).");
      lines.push("  - <configuration> contains the full payload blueprint for that report.");
      lines.push("-->");
      lines.push(
        `<operatorReportConfigurations generatedAt="${escapeXml(payload.generated_at)}" totalLicenses="${payload.total_licenses}" totalReports="${payload.total_reports}">`,
      );

      payload.licenses.forEach((license) => {
        lines.push("  ");
        lines.push(
          `  <!-- License: ${escapeXml(license.license_name)} (${escapeXml(license.license_id)}) -->`,
        );
        lines.push(
          `  <license recordId="${escapeXml(license.license_record_id)}" code="${escapeXml(license.license_code)}" id="${escapeXml(license.license_id)}" group="${escapeXml(license.group)}" groupLabel="${escapeXml(license.group_label)}">`,
        );
        lines.push(`    <licenseName>${escapeXml(license.license_name)}</licenseName>`);

        license.reports.forEach((report) => {
          lines.push(
            `    <!-- Report: ${escapeXml(report.report_name)} | Configurable: ${report.is_configurable ? "Yes" : "No"} -->`,
          );
          lines.push(
            `    <report id="${escapeXml(report.report_id)}" name="${escapeXml(report.report_name)}" configurable="${report.is_configurable ? "true" : "false"}">`,
          );
          if ((report.game_unique_names || []).length) {
            lines.push("      <selectedGameCategories>");
            (report.game_unique_names || []).forEach((cat) => {
              lines.push(`        <gameCategory>${escapeXml(cat)}</gameCategory>`);
            });
            lines.push("      </selectedGameCategories>");
          } else {
            lines.push("      <selectedGameCategories />");
          }
          const reportXml = this.convertJsonToXml(report.configuration);
          lines.push("      <configuration><![CDATA[");
          lines.push(reportXml);
          lines.push("      ]]></configuration>");
          lines.push("    </report>");
        });

        lines.push("  </license>");
      });
      lines.push("</operatorReportConfigurations>");

      return lines.join("\n");
    },

    triggerDownload(content, fileName, mimeType) {
      const blob = new Blob([content], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    async exportAllConfigurations() {
      if (!this.storedConfigurations.length) {
        this.showNotification("No report configurations available to export.", "error");
        return;
      }

      this.isExportingAll = true;
      try {
        const payload = this.buildAllConfigurationsPayload();
        const now = new Date();
        const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}`;

        if (this.bulkExportFormat === "xml") {
          const xml = this.buildAllConfigurationsXml(payload);
          this.triggerDownload(xml, `report-configurations-${stamp}.xml`, "application/xml;charset=utf-8");
          this.showNotification("XML export downloaded successfully.", "success");
        } else {
          const json = JSON.stringify(payload, null, 2);
          this.triggerDownload(json, `report-configurations-${stamp}.json`, "application/json;charset=utf-8");
          this.showNotification("JSON export downloaded successfully.", "success");
        }
      } catch (error) {
        console.error("Export failed:", error);
        this.showNotification("Failed to export configurations.", "error");
      } finally {
        this.isExportingAll = false;
      }
    },
    openExportModal() {
      this.showExportModal = true;
    },
    async confirmExportAll() {
      await this.exportAllConfigurations();
      this.showExportModal = false;
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      if (this.isFullscreen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    showNotification(message, type = "success") {
      // Remove any existing notifications
      const existingAlerts = document.querySelectorAll(".fixed-notification");
      existingAlerts.forEach((alert) => alert.remove());

      const notification = document.createElement("div");

      // Base classes
      notification.className =
        "alert alert-dismissible fade show fixed-notification";

      // Set styles based on type
      if (type === "success") {
        notification.style.background =
          "linear-gradient(310deg, #4caf50 0%, #8bc34a 100%)";
        notification.style.color = "white";
        notification.style.border = "none";
        notification.style.boxShadow =
          "0 4px 20px 0 rgba(76, 175, 80, 0.14), 0 7px 10px -5px rgba(76, 175, 80, 0.4)";
      } else if (type === "error") {
        notification.style.background =
          "linear-gradient(310deg, #f44336 0%, #f88a82 100%)";
        notification.style.color = "white";
        notification.style.border = "none";
        notification.style.boxShadow =
          "0 4px 20px 0 rgba(244, 67, 54, 0.14), 0 7px 10px -5px rgba(244, 67, 54, 0.4)";
      }

      // Position styles
      notification.style.position = "fixed";
      notification.style.top = "20px";
      notification.style.right = "20px";
      notification.style.zIndex = "9999";
      notification.style.minWidth = "300px";

      // Create content
      const strong = document.createElement("strong");
      strong.textContent = type === "error" ? "Error! " : "Success! ";
      notification.appendChild(strong);

      const textNode = document.createTextNode(message);
      notification.appendChild(textNode);

      const closeButton = document.createElement("button");
      closeButton.type = "button";
      closeButton.className = "btn-close";
      closeButton.style.filter = "brightness(0) invert(1)";
      closeButton.setAttribute("data-bs-dismiss", "alert");
      closeButton.setAttribute("aria-label", "Close");

      notification.appendChild(closeButton);
      document.body.appendChild(notification);

      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 3000);
    },
    escapeHtml(str = "") {
      return str
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    annotateFieldHelp(highlightedHtml = "", format = "json", reportId = "") {
      if (!highlightedHtml) return highlightedHtml;

      if (format === "json") {
        const keyTokenRegex =
          /<span class="hljs-(?:attr|property|string)">("?)([^"<]+)\1<\/span>/g;

        return highlightedHtml.replace(
          keyTokenRegex,
          (fullMatch, _quote, fieldName, offset, sourceHtml) => {
            const tail = sourceHtml.slice(offset + fullMatch.length, offset + fullMatch.length + 180);
            const isKey = /^(?:\s|<[^>]+>)*(?::|<span class="hljs-punctuation">:<\/span>)/.test(
              tail,
            );

            if (!isKey) return fullMatch;

            const help = resolveFieldHelp({
              reportId,
              key: fieldName,
              path: fieldName,
            });
            return `<span class="field-help-token" data-help="${this.escapeHtmlAttribute(help)}">${fullMatch}</span>`;
          },
        );
      }

      return highlightedHtml.replace(
        /<span class="hljs-name">([a-zA-Z_][\w:-]*)<\/span>/g,
        (fullMatch, fieldName) => {
          const help = resolveFieldHelp({
            reportId,
            key: fieldName,
            path: fieldName,
          });
          return `<span class="field-help-token" data-help="${this.escapeHtmlAttribute(help)}">${fullMatch}</span>`;
        },
      );
    },
    escapeHtmlAttribute(value = "") {
      return String(value)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },

    addXmlListComments(xmlStr = "", reportId = "") {
      if (!xmlStr) return "";

      const listTagMapping = {
        game_summary: ["gameSummary", "gameMovementList", "gameSummaryDetail"],
        game_funds_in_play: [
          "gameFundsInPlay",
          "gameMovementList",
          "gameInPlayDetail",
        ],
        land_summary: ["movementList", "movements", "meterList", "meters"],
        terminal_config: [
          "terminalConfig",
          "installedGameList",
          "installedGame",
        ],
      };

      const tags = listTagMapping[reportId] || [];

      let result = xmlStr;
      tags.forEach((tag) => {
        const re = new RegExp(`(<\\s*${tag}[^>]*>)`, "gi");
        result = result.replace(
          re,
          `<!-- list: multiple entries allowed for <${tag}> -->\n$1`,
        );
      });

      return result;
    },
  },
  beforeUnmount() {
    if (this.isFullscreen) {
      document.body.style.overflow = "";
    }
  },
};
</script>

<style scoped>
.report-preview-page {
  min-height: 100vh;
}

.bg-gradient-success {
  background: linear-gradient(310deg, #4caf50 0%, #8bc34a 100%);
}

.bg-gradient-primary {
  background: linear-gradient(310deg, #f44335 0%, #ef5350 100%);
}

.shadow-primary {
  box-shadow:
    0 4px 20px 0 rgba(0, 0, 0, 0.14),
    0 7px 10px -5px rgba(76, 175, 80, 0.4);
}

.border-radius-lg {
  border-radius: 0.5rem;
}

.z-index-2 {
  z-index: 2;
}

.preview-content-wrapper {
  background: #ffffff;
  border-radius: 0.5rem;
  padding: 1.5rem;
}

.selection-section,
.preview-section,
.empty-section,
.loading-section {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 1rem;
}

.export-report-list {
  max-height: 120px;
  overflow-y: auto;
}

.section-header {
  background: #f8f9fa;
  padding-left: 1rem;
  padding-right: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.section-body {
  padding-left: 1rem;
  padding-right: 1rem;
}

.preview-tabs {
  display: flex;
  border-bottom: none;
}

.preview-tab {
  padding: 0.25rem 0.75rem;
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  font-size: 0.75rem;
  border-radius: 0.25rem;
  margin-left: 0.5rem;
}

.preview-tab.active {
  color: #4caf50;
  border-bottom-color: #4caf50;
  font-weight: bold;
  background: #f8f9fa;
}

.preview-container {
  background: #f8f9fa;
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid #e9ecef;
}

.preview-header {
  padding: 0.5rem 0;
}

.code-display-box {
  background: #282a36;
  border-radius: 0.5rem;
  padding: 1rem;
  max-height: 500px;
  overflow-y: auto;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", "Consolas", monospace;
  font-size: 1rem;
  color: #f8f8f2;
  line-height: 1.6;
  margin: 0;
  white-space: pre;
  word-break: normal;
  overflow-x: auto;
  border: 1px solid #44475a;
  scrollbar-width: thin;
  scrollbar-color: #5e6a84 #1f2431;
  scrollbar-gutter: stable both-edges;
}

.code-display-box::-webkit-scrollbar {
  width: 9px;
  height: 9px;
}

.code-display-box::-webkit-scrollbar-track {
  background: #1f2431;
  border-radius: 10px;
}

.code-display-box::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #6c7a96 0%, #56627a 100%);
  border-radius: 10px;
  border: 2px solid #1f2431;
}

.code-display-box::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #7d8da9 0%, #5f6f8d 100%);
}

.code-display-box code {
  background: transparent;
  padding: 0;
  font-size: inherit;
  color: inherit;
  white-space: pre;
}

/* Hide inner highlight.js scrollbar while keeping scroll functionality. */
.code-display-box code.hljs {
  overflow: auto !important;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.code-display-box code.hljs::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.code-display-box :deep(.field-help-token) {
  position: relative;
  cursor: help;
  text-decoration: underline dotted rgba(160, 198, 255, 0.85);
  text-underline-offset: 2px;
  transition: background-color 0.12s ease;
}

.code-display-box :deep(.field-help-token):hover {
  background: rgba(160, 198, 255, 0.14);
  border-radius: 3px;
}

.code-display-box :deep(.field-help-token)::after {
  content: attr(data-help);
  position: absolute;
  left: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%);
  width: 300px;
  max-width: 70vw;
  background: #ffffff;
  color: #1f2937;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px;
  font-size: 12px;
  line-height: 1.35;
  white-space: normal;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  z-index: 100;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.24);
}

.code-display-box :deep(.field-help-token):hover::after {
  opacity: 1;
}

/* Fullscreen Overlay Styles */
.fullscreen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #f8f9fa;
  z-index: 9999;
  display: flex;
  flex-direction: column;
}

.fullscreen-overlay .fullscreen-header {
  background: #ffffff;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.fullscreen-overlay .fullscreen-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.fullscreen-overlay .fullscreen-content {
  flex: 1;
  overflow: auto;
  padding: 1.5rem;
}

.code-display-box.fullscreen {
  max-height: none;
  height: 100%;
  border: none;
  border-radius: 0.5rem;
  background: #282a36;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
}

.empty-state-icon {
  opacity: 0.5;
}

.form-select-sm {
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

.fixed-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  min-width: 300px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Modal Styles */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-dialog {
  background: white;
  border-radius: 0.5rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.modal-content {
  padding: 0;
}

.modal-header {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.btn-close {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 1.5rem;
  height: 1.5rem;
  opacity: 0.5;
}

.btn-close:hover {
  opacity: 1;
}

.btn-close::before {
  content: "×";
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

@media (max-width: 767.98px) {
  .preview-content-wrapper {
    padding: 1rem;
  }

  .code-display-box {
    font-size: 0.75rem;
    padding: 0.75rem;
  }

  .preview-tab {
    padding: 0.2rem 0.5rem;
    font-size: 0.7rem;
  }

  .modal-dialog {
    width: 95%;
  }
}

@media (max-width: 575.98px) {
  .preview-content-wrapper {
    padding: 0.75rem;
  }

  .section-body,
  .section-header {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .preview-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style>
